<template>
    <v-app>
        <v-main v-if="organization_account != null">

            <m-make-payment-app-bar
                :value="organization_label"
                :banner_branding="banner_branding"
            />

            <v-container>
                <div style="margin-left: 15px; margin-right: 15px">

                    <div
                        v-if="show_thank_you"
                        style="text-align: center; margin-top: 30px; font-size: 32px"
                    >
                        Thanks!<br /><br />We will be in touch soon.
                    </div>

                    <div v-else>
                        <p style="margin-top: 30px; margin-bottom: 30px">
                            Enter your information to receive text messages from {{ organization_account.label }}
                        </p>

                        <rc-base-input
                            type="text"
                            v-model="first_name"
                            label="First Name"
                            autocomplete="given-name"
                            required
                        />

                        <rc-base-input
                            type="text"
                            v-model="last_name"
                            label="Last Name"
                            autocomplete="family-name"
                            required
                        />

                        <rc-base-input
                            type="tel"
                            v-model="phone_number"
                            label="Phone Number"
                            required
                            @validated="phone_input_valid = $event"
                        />

                        <div v-if="robust_age_gate">
                            <p>You must be 21 or older to receive our text messages. Please enter your birthday in
                                MM/DD/YYYY format so we can verify your age
                            </p>

                            <rc-base-input
                                v-model="birthday"
                                label="Birthday"
                                type="birthday"
                                required
                                @validated="birthday_input_valid = $event"
                            />
                        </div>

                        <div
                            v-if="organization_account != null"
                            class="consent-checkboxes"
                        >
                            <m-legal-agreement
                                ref="conversational_agreement"
                                v-model="conversational_consent"
                                :organization_id="organization_id"
                                type="toll-free-opt-in-form-conversational-agreement"
                                class="consent-checkbox"
                                required
                            />

                            <m-legal-agreement
                                ref="informational_agreement"
                                v-model="informational_consent"
                                :organization_id="organization_id"
                                type="toll-free-opt-in-form-informational-agreement"
                                class="consent-checkbox"
                                required
                            />

                            <m-legal-agreement
                                ref="promotional_agreement"
                                v-model="promotional_consent"
                                :organization_id="organization_id"
                                type="toll-free-opt-in-form-promotional-agreement"
                                class="consent-checkbox"
                                required
                            />
                        </div>

                        <rc-alert
                            v-model="show_alert"
                            color="primary"
                            type="error"
                            dismissible
                            style="margin-top: 15px"
                            :restore-focus-to="$refs.submitButton"
                        >
                            {{ alert_text }}
                        </rc-alert>

                        <div style="text-align: right; padding-top: 30px">
                            <v-btn
                                ref="submitButton"
                                color="secondary"
                                :disabled="!is_valid"
                                @click="onClickSubmit"
                            >
                                Submit
                            </v-btn>
                        </div>
                    </div>

                </div>
            </v-container>

        </v-main>

        <m-global-spinner />
        <m-global-snackbar />
        <m-global-alert />
        <m-global-confirm />
        <m-global-prompt />
    </v-app>
</template>

<script>
import RcBaseInput from '@/components/shared/inputs/rc-base-input.vue';
import RcAlert from '@/components/shared/alerts/rc-alert.vue';

export default {
    components: {
        RcBaseInput,
        RcAlert
    },

    data() {
        return {
            banner_branding: null,
            organization_account: null,
            conversational_consent: false,
            informational_consent: false,
            promotional_consent: false,

            first_name: null,
            last_name: null,
            phone_number: null,
            phone_input_valid: false,
            birthday: null,
            birthday_input_valid: false,

            show_alert: false,
            alert_text: null,

            show_thank_you: false,
        }
    },

    mounted() {
        this.loadProfile();
        document.title = `Opt-In Form ${this.organization_label ? '| ' + this.organization_label : ''}`;
    },

    methods: {
        loadProfile() {
            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/opt-in-form/toll-free/profile',

                params: {
                    organization_id: this.$route.params.organization_id,
                    banner_id: this.$route.params.banner_id,
                },

                on_success: (data) => {
                    this.banner_branding = data.banner_branding;
                    this.organization_account = data.organization_account;
                },

                on_error: 'Unable to get form information',
            });
        },

        onClickSubmit() {
            if (!this.is_valid) {
                // Should not be possible...
                this.alert_text = 'Please fill out all of the above fields.';
                this.show_alert = true;
                return;
            }

            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/opt-in-form/toll-free/opt-in',

                params: {
                    organization_id: this.$route.params.organization_id,
                    first_name: this.first_name,
                    last_name: this.last_name,
                    phone_number: this.phone_number,
                    birthday: this.birthday,
                    conversational_agreement_specimen: this.conversational_consent ? this.$refs['conversational_agreement'].getSpecimen() : '',
                    informational_agreement_specimen: this.informational_consent ? this.$refs['informational_agreement'].getSpecimen() : '',
                    promotional_agreement_specimen: this.promotional_consent ? this.$refs['promotional_agreement'].getSpecimen() : '',
                    conversational_consent: this.conversational_consent,
                    informational_consent: this.informational_consent,
                    promotional_consent: this.promotional_consent,
                    submitted_from_url: '' + document.location,
                },

                on_success: (data) => {

                    if (data.valid == false) {
                        this.alert_text = data.message;
                        this.show_alert = true;
                        return;
                    }

                    this.show_thank_you = true;

                },

                on_error: 'Unable to opt in',
            });
        },

        isBlank(value) {
            if (value == null) return true;
            if (value.trim().length == 0) return true;
            return false;
        },
    },

    computed: {
        robust_age_gate() {
            if (this.organization_account == null)
                return false;

            return this.organization_account.industry == 'wine';
        },

        is_valid() {
            // At least one consent type must be selected
            if (!this.conversational_consent && !this.informational_consent && !this.promotional_consent) return false;

            if (this.isBlank(this.first_name)) return false;
            if (this.isBlank(this.last_name)) return false;
            if (this.isBlank(this.phone_number) || !this.phone_input_valid) return false;
            if ((this.isBlank(this.birthday) || !this.birthday_input_valid) && this.robust_age_gate) return false;

            return true;
        },

        organization_label() {
            if (this.organization_account == null) return '';
            return this.organization_account.label;
        },

        organization_id() {
            if (this.organization_account == null) return '';
            return this.organization_account.organization_id;
        },
    },
}
</script>

<style lang="scss" scoped>
.consent-checkboxes {
    margin-top: 2rem;
    margin-bottom: 20px;
}

.consent-checkbox {
    margin-bottom: 8px;
}
:deep(.v-input--selection-controls) {
    margin-top: 0px;
}
:deep(.label_container) {
    padding-top: 0px !important;
}
:deep(.outer_legal_container) {
    align-items: flex-start !important;
}

/*
    When used as text/bg with white text, our primary color is not AA compliant on a white bg.
    This should be moved to global.css eventually ...
    But that will necessitate more work than just these styles for consistency sake.
    Only applying to public forms for now.
*/
div.v-application .primary--text {
    color: var(--v-primary-darken1) !important;
    caret-color: var(--v-primary-darken1) !important;
}

div.v-application .error--text {
    color: var(--v-error-darken1) !important;
    caret-color: var(--v-error-darken1) !important;
}

div.v-alert.primary {
    background-color: var(--v-primary-darken1) !important;
    border-color: var(--v-primary-darken1) !important;
}
</style>
